import { ChangeEvent, FC } from 'react';
import { useRef } from 'react';
import { Button } from '@/components/ui';
import { X, Upload } from 'lucide-react';
import api from '@/utils/api';

interface ImageUploadFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onUpload: (file: File) => Promise<void>;
  uploading: boolean;
  disabled?: boolean;
  required?: boolean;
  getImageUrl?: (path: string) => string; // Optional helper to get display URL
}

const defaultGetImageUrl = (path: string): string => {
  if (!path) return '';
  // If already a full URL (http/https), return as-is (includes signed URLs and external URLs)
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it's a local path (starts with /), prepend baseURL
  if (path.startsWith('/')) {
    const baseURL = api.defaults.baseURL || '';
    return `${baseURL}${path}`;
  }
  // If it's an S3 key (like "images/abc-123.png"), return as-is
  // The getImageUrl prop will handle converting to signed URL if available
  return path;
};

export const ImageUploadField: FC<ImageUploadFieldProps> = ({
  label,
  value,
  onChange,
  onUpload,
  uploading,
  disabled = false,
  required = false,
  getImageUrl = defaultGetImageUrl,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await onUpload(file);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-slate-600 mb-1.5 capitalize">
        {label} {required && '*'}
      </label>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploading || disabled}
      />
      <div className="border border-slate-200 rounded-lg p-4 bg-gray-50">
        {value ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <img
                src={getImageUrl(value)}
                alt={label}
                className="max-h-20 max-w-32 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <div className="flex gap-2">
              {!disabled && (
                <>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="ghost"
                    size="sm"
                    disabled={uploading}
                    className="text-xs"
                  >
                    {uploading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-1" /> Change</>}
                  </Button>
                  <Button
                    onClick={() => onChange('')}
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[100px] gap-2">
            <span className="text-sm text-gray-400">No {label.toLowerCase()} uploaded</span>
            {!disabled && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="secondary"
                size="sm"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-1" /> Upload {label}</>}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

